// Connect7 — admin module registry + access-level resolution.
// Modules are keyed by their admin route (e.g. "/admin/sip-trunks"). The
// super_super_admin can set a per-tenant access level for each module and role
// (admin / super_admin): hidden | view | edit | full. Defaults mirror the
// historical hard-coded requireRole gating so behaviour is unchanged until an
// operator edits the matrix.
import { ADMIN_NAV } from "@/admin/admin-nav";

export const LEVELS = ["hidden", "view", "edit", "full"];
export const LEVEL_RANK = { hidden: 0, view: 1, edit: 2, full: 3 };
export const LEVEL_LABEL = {
  hidden: "Hidden", view: "View only", edit: "Edit", full: "Full (delete)",
};
export const MANAGED_ROLES = ["admin", "super_admin"];
export const SUPER_SUPER = "super_super_admin";

// Modules that stay super_super_admin-only and are NOT configurable in the
// matrix (the Operator section). Everything else is configurable.
const EXCLUDE_SECTIONS = new Set(["Operator"]);

// Hidden-by-default for admin/super_admin (mirror backend core/access.py).
export const DEFAULT_HIDDEN = new Set([
  "/admin/agent-layouts", "/admin/llm-config", "/admin/stt-config",
  "/admin/ai-prompts", "/admin/pii-rules", "/admin/qm-samples",
  "/admin/coaching", "/admin/calibration",
  "/admin/integrations", "/admin/integrations-hub", "/admin/webhooks",
  "/admin/api-keys", "/admin/crm-mappings",
  "/admin/ticket-queues", "/admin/ticket-categories", "/admin/sla-policies",
  "/admin/branding", "/admin/retention", "/admin/ip-allowlist",
  "/admin/billing",
]);

const moduleKey = (to) => (to || "").split("?")[0];

// Configurable modules grouped by section (for the Access Control editor UI).
export const MODULE_SECTIONS = ADMIN_NAV
  .filter((s) => !EXCLUDE_SECTIONS.has(s.label))
  .map((s) => {
    const seen = new Set();
    const items = [];
    for (const it of s.items || []) {
      const key = moduleKey(it.to);
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({ key, label: it.label });
    }
    return { label: s.label, items };
  })
  .filter((s) => s.items.length > 0);

// Flat list of all module route keys (incl. Operator) for path matching.
export const ALL_MODULE_KEYS = (() => {
  const keys = new Set();
  for (const s of ADMIN_NAV) {
    for (const it of s.items || []) keys.add(moduleKey(it.to));
  }
  return [...keys];
})();

export function resolveLevel(role, key, overrides) {
  if (role === SUPER_SUPER) return "full";
  if (!MANAGED_ROLES.includes(role)) return "full";
  const ov = overrides?.[key];
  if (LEVELS.includes(ov)) return ov;
  return DEFAULT_HIDDEN.has(key) ? "hidden" : "full";
}

// Longest matching module key for the current browser pathname.
export function moduleForPath(pathname) {
  let best = null;
  for (const k of ALL_MODULE_KEYS) {
    if (pathname === k || pathname.startsWith(k + "/")) {
      if (!best || k.length > best.length) best = k;
    }
  }
  return best;
}
